import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '../services/auth'
import { tokenUtils } from '../services/api'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      customer: null,
      isAuthenticated: false,
      isLoading: true,
      isGuest: true, // 访客模式标识

      // Initialize auth state - 支持访客模式
      init: async () => {
        // 检查本地是否有有效 token
        if (!tokenUtils.isLoggedIn()) {
          // 无 token，直接设置为访客模式
          set({
            isAuthenticated: false,
            isLoading: false,
            isGuest: true,
            user: null,
            customer: null,
          })
          return
        }

        // 有 token，验证并获取用户信息
        try {
          const customerInfo = await authService.getCurrentCustomer()
          if (customerInfo) {
            set({
              user: customerInfo.email || customerInfo.phone,
              customer: customerInfo,
              isAuthenticated: true,
              isLoading: false,
              isGuest: false,
            })
          } else {
            // Token 无效
            tokenUtils.clearTokens()
            set({
              isAuthenticated: false,
              isLoading: false,
              isGuest: true,
              user: null,
              customer: null,
            })
          }
        } catch (error) {
          // 验证失败，清除 token
          tokenUtils.clearTokens()
          set({
            isAuthenticated: false,
            isLoading: false,
            isGuest: true,
            user: null,
            customer: null,
          })
        }
      },

      // Login
      login: async (account, password) => {
        const response = await authService.login(account, password)
        const customerInfo = await authService.getCurrentCustomer()
        set({
          user: customerInfo?.email || account,
          customer: customerInfo,
          isAuthenticated: true,
          isGuest: false,
        })
        return response
      },

      // Logout
      logout: async () => {
        await authService.logout()
        set({
          user: null,
          customer: null,
          isAuthenticated: false,
          isGuest: true,
        })
      },

      // Update customer info
      setCustomer: (customer) => set({ customer }),

      // Refresh customer data
      refreshCustomer: async () => {
        try {
          const customerInfo = await authService.getCurrentCustomer()
          set({ customer: customerInfo })
        } catch (error) {
          console.error('Refresh customer failed:', error)
        }
      },

      // Check if user has a specific role
      hasRole: (role) => {
        const { isAuthenticated } = get()
        if (!isAuthenticated) return false
        // For now, all portal users are clients
        return role === 'Logistics Client'
      },
    }),
    {
      name: 'auth-storage',
      /*
       * 【不要把 isAuthenticated / isGuest 持久化】
       * 它们是「认证结论」，唯一依据是 token 当下还有效 —— 必须每次由 init() 重新得出。
       * 原先持久化了 isAuthenticated：刷新后它先从 localStorage 恢复成 true，
       * 而 init() 的校验是异步的还没跑完，于是 `enabled: isAuthenticated` 的列表查询
       * 抢在校验之前发了出去；token 若已过期就是 401，用户被拦截器踢去登录页。
       * 只持久化 user（仅用于登录前显示上次的账号，不参与鉴权判断）。
       */
      partialize: (state) => ({
        user: state.user,
      }),
    }
  )
)

export default useAuthStore
